import { Injectable, inject } from '@angular/core';
import {
  ActivatedRoute,
  Data,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import * as Sentry from '@sentry/angular';
import { Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';

/**
 * Service to track router navigation events with enhanced context
 * Provides better visibility into user navigation patterns in Sentry
 */
@Injectable({ providedIn: 'root' })
export class SentryRouterTrackerService {
  private router = inject(Router);

  private navigationStartTime = 0;
  private navigationStartSubscription: Subscription = new Subscription();
  private navigationEndSubscription: Subscription = new Subscription();
  private navigationErrorSubscription: Subscription = new Subscription();
  debounceTime = 500;
  navigationCancelSubscription: Subscription = new Subscription();

  /**
   * Initialize router tracking
   * Call this in your AppComponent or main initialization
   */
  init(): void {
    this.clearSubscriptions();
    this.trackNavigationStart();
    this.trackNavigationCancel();
    this.trackNavigationEnd();
    this.trackNavigationError();
  }

  /**
   * Track navigation start events
   */
  private trackNavigationStart(): void {
    this.navigationStartSubscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        debounceTime(this.debounceTime),
      )
      .subscribe((event: NavigationStart) => {
        this.navigationStartTime = performance.now() - this.debounceTime;

        Sentry.addBreadcrumb({
          category: 'navigation',
          message: `Navigation started to ${event.url}`,
          level: 'info',
          data: {
            url: event.url,
            navigationTrigger: event.navigationTrigger,
          },
        });
      });
  }

  /**
   * Track navigation cancel events
   */
  private trackNavigationCancel(): void {
    this.navigationCancelSubscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationCancel),
        debounceTime(this.debounceTime),
      )
      .subscribe((event: NavigationCancel) => {
        const navigationDuration = this.navigationStartTime
          ? performance.now() - this.navigationStartTime - this.debounceTime
          : 0;

        Sentry.addBreadcrumb({
          category: 'navigation',
          message: `Navigation cancelled to ${event.url}`,
          level: 'warning',
          data: {
            url: event.url,
            reason: event.reason,
            duration: navigationDuration,
          },
        });

        this.navigationStartTime = 0;
      });
  }

  /**
   * Track successful navigation end events
   */
  private trackNavigationEnd(): void {
    this.navigationEndSubscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        debounceTime(this.debounceTime),
      )
      .subscribe((event: NavigationEnd) => {
        const navigationDuration = this.navigationStartTime
          ? performance.now() - this.navigationStartTime - this.debounceTime
          : 0;

        // Get route snapshot for params
        const route = this.router.routerState.root;
        const routeData = this.getRouteData(route);

        // Set transaction name (with :id for grouping)
        const transactionName = this.getTransactionName(
          event.urlAfterRedirects,
        );
        Sentry.getCurrentScope().setTransactionName(transactionName);

        // Set tags from route
        Sentry.setTags({
          current_page: transactionName,
          page_category: this.getPageCategory(transactionName),
          ...routeData.tags,
        });

        // Set context from route params
        Sentry.setContext('navigation', {
          from: event.url,
          to: event.urlAfterRedirects,
          duration: navigationDuration,
          params: routeData.params,
          queryParams: routeData.queryParams,
          data: routeData.data,
        });

        Sentry.addBreadcrumb({
          category: 'navigation',
          message: `Navigated to ${event.urlAfterRedirects}`,
          level: 'info',
          data: {
            from: event.url,
            to: event.urlAfterRedirects,
            duration: navigationDuration,
            paramId: routeData.params['id'],
          },
        });

        // Track slow navigation
        if (navigationDuration > 2000) {
          Sentry.addBreadcrumb({
            category: 'performance',
            message: `Slow navigation: ${navigationDuration.toFixed(0)}ms`,
            level: 'warning',
            data: {
              url: event.urlAfterRedirects,
              duration: navigationDuration,
            },
          });
        }
      });
  }

  /**
   * Track navigation errors
   */
  private trackNavigationError(): void {
    this.navigationErrorSubscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationError),
        debounceTime(this.debounceTime),
      )
      .subscribe((event: NavigationError) => {
        Sentry.addBreadcrumb({
          category: 'navigation',
          message: `Navigation error to ${event.url}`,
          level: 'error',
          data: {
            url: event.url,
            error: event.error?.toString(),
          },
        });

        // Capture the error
        Sentry.captureException(event.error, {
          tags: {
            navigation_error: 'true',
            url: event.url,
          },
        });
      });
  }

  /**
   * Get a clean transaction name from URL
   * Removes IDs and query parameters for better grouping
   */
  private getTransactionName(url: string): string {
    try {
      // Remove query parameters
      const urlWithoutQuery = url.split('?')[0];

      // Replace UUIDs and numeric IDs with placeholders
      const cleanUrl = urlWithoutQuery
        .replace(
          /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
          '/:id',
        )
        .replace(/\/\d+/g, '/:id');

      return cleanUrl || '/';
    } catch {
      return url;
    }
  }

  // Helper to extract route data
  private getRouteData(route: ActivatedRoute | null): {
    params: { [key: string]: string };
    queryParams: { [key: string]: string };
    data: { [key: string]: Data };
    tags: { [key: string]: string };
  } {
    let child = route;
    const params = {};
    const queryParams = {};
    const data = {};
    const tags = {};

    while (child) {
      // Collect params from all route segments
      if (child.snapshot.params) {
        Object.assign(params, child.snapshot.params);
      }
      // Collect query params
      if (child.snapshot.queryParams) {
        Object.assign(queryParams, child.snapshot.queryParams);
      }
      // Collect route data (you can add custom data in route config)
      if (child.snapshot.data) {
        Object.assign(data, child.snapshot.data);
        // If route has sentryTags, add them
        if (child.snapshot.data['sentryTags']) {
          Object.assign(tags, child.snapshot.data['sentryTags']);
        }
      }
      child = child.firstChild;
    }

    return { params, queryParams, data, tags };
  }

  // Helper to get page category
  private getPageCategory(url: string): string {
    const segments = url.split('/').filter((s) => s);
    if (segments.length === 0) return 'home';

    // First meaningful segment is usually the category
    return segments[0].split('?')[0]; // e.g., 'shifts', 'projects', 'services'
  }

  public clearSubscriptions(): void {
    this.navigationStartSubscription?.unsubscribe();
    this.navigationEndSubscription?.unsubscribe();
    this.navigationErrorSubscription?.unsubscribe();
  }
}
